// Native Aimdoc event catalog for webhook subscriptions — mirrors
// backend/api/services/native_events.py. Subscriptions are a {name: boolean}
// map over these names; the order here is the display order.

export type WebhookEvents = Record<string, boolean>;

export interface WebhookEventDefinition {
    name: string;
    label: string;
    description: string;
}

export const WEBHOOK_EVENTS: WebhookEventDefinition[] = [
    { name: 'new_session', label: 'New Session', description: 'Triggered when a new chat session starts' },
    { name: 'new_lead', label: 'New Contact', description: 'Triggered when a contact is captured' },
    { name: 'session_active', label: 'Session Active', description: 'Triggered when a session becomes active' },
    { name: 'session_inactive', label: 'Session Inactive', description: 'Triggered when a session goes inactive (the conversation ended)' },
    { name: 'session_escalated', label: 'Session Escalated', description: 'Triggered when a session is escalated' },
    { name: 'escalation_missed', label: 'Escalation Missed', description: 'Triggered when an escalation could not be taken by anyone' },
    { name: 'rep_took_over_chat', label: 'Rep Took Over Chat', description: 'Triggered when a human rep takes over a live conversation' },
    { name: 'buyer_intent_detected', label: 'Buyer Intent Detected', description: 'Triggered when buyer intent is detected' },
    { name: 'calendar_shown', label: 'Calendar Shown', description: 'Triggered when the agent shows a booking calendar to a visitor' },
    { name: 'meeting_booked', label: 'Meeting Booked', description: 'Triggered when a visitor books a meeting' },
    { name: 'new_question', label: 'New Question', description: 'Triggered when a new question is asked' },
    { name: 'visitor_identified', label: 'Visitor Identified', description: 'Triggered when a visitor is identified or materially enriched' },
    { name: 'visitor_warm', label: 'Warm Visitor', description: "Triggered when a visitor's browsing shows buying intent: a pricing, demo or contact page, product-page depth, a return visit or a buying call to action" },
    { name: 'visitor_classified', label: 'Visitor Classified', description: 'Triggered when a visitor is classified into a track' },
    { name: 'lead_updated', label: 'Contact Updated', description: "Triggered when a contact's details are updated during a conversation" },
    { name: 'account_researched', label: 'Account Researched', description: "Triggered when account research finishes and scores an account's fit" },
    { name: 'service_run_completed', label: 'Service Run Completed', description: 'Triggered when a service run finishes with a result' },
    { name: 'service_run_progress', label: 'Service Run Progress', description: "Triggered when a service run's user-facing plan changes" },
    { name: 'service_run_failed', label: 'Service Run Failed', description: 'Triggered when a service run ends in an error' },
    { name: 'session_flagged', label: 'Session Flagged', description: 'Triggered when session moderation finds a visitor acting in bad faith' },
    { name: 'email_conversation_started', label: 'Email Conversation Started', description: 'Triggered when a new inbound email conversation starts on the email channel' },
];

export const WEBHOOK_EVENT_DESCRIPTIONS: Record<string, string> = Object.fromEntries(
    WEBHOOK_EVENTS.map((event) => [event.name, event.description])
);

export const WEBHOOK_EVENT_DISPLAY_NAMES: Record<string, string> = Object.fromEntries(
    WEBHOOK_EVENTS.map((event) => [event.name, event.label])
);

export const emptyWebhookEvents = (): WebhookEvents =>
    Object.fromEntries(WEBHOOK_EVENTS.map((event) => [event.name, false]));
